import { Injectable } from '@angular/core';
import * as waxjs from '@waxio/waxjs/dist';
import {EMPTY, interval, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {expand, flatMap, map, reduce, retry, startWith, takeLast, tap} from 'rxjs/operators';


export interface TableRowUser {
  code: 'eosio';
  scope: string;
  table: 'userres';
  payer: string;
  count: number;
}
export interface TableRowsResult<T> {
  rows: T[];
  more: boolean;
}

export interface TableRowDrop {
  drop_id: number;
  collection_name: string;
  assets_to_mint: {
    template_id: number;
  }[];
  listing_price: string;
  settlement_symbol: string;
  price_recipient: string;
  fee_rate: number;
  auth_required: number;
  account_limit: number;
  account_limit_cooldown: number;
  max_claimable: number;
  current_claimed: number;
  start_time: number;
  end_time: number;
  display_data: string;
}

export interface TableRowPrice {
  id: number;
  median: number;
  owner: string;
  timestamp: string;
  value: number;
}

export interface WaxAccount {
  account_name: string;
  head_block_num: number;
  head_block_time: string;
  last_code_update: string;
  created: string;
  refund_request: RefundRequest;
  ram_quota: string | number;
  net_limit: ResourceLimit;
  total_resources: ResourceOverview;
  core_liquid_balance: string;
  self_delegated_bandwidth: ResourceDelegation;
  net_weight: string;
  cpu_weight: string;
  ram_usage: string;
  privileged: boolean;
  permissions: Permission[];
  voter_info: VoterInfo;
}
export interface Wait {
  wait_sec: number;
  weight: number;
}
export interface KeyAuthority {
  key: string;
  weight: number;
}
export interface AccountAuthority {
  actor: string;
  permission: string;
}
export interface RequiredAuthority {
  waits: Wait[];
  keys: KeyAuthority[];
  threshold: number;
  accounts: AccountAuthority[];
}

export interface Permission {
  parent: string;
  perm_name: string;
  required_auth: RequiredAuthority;
}

export interface VoterInfo {
  owner: string;
  proxy: string;
  producers: string[];
  staked: string;
  last_vote_weight: string;
  proxied_vote_weight: string;
  is_proxy: number;
  flags1: number;
  reserved2: number;
  reserved3: string;
}

export interface ResourceDelegation {
  from: string;
  to: string;
  net_weight: string;
  cpu_weight: string;
}

export interface RefundRequest {
  owner: string;
  request_time: string;
  net_amount: string;
  cpu_amount: string;
}

export interface ResourceLimit {
  max: string;
  available: string;
  used: string;
}

export interface ResourceOverview {
  owner: string;
  ram_bytes: string;
  net_weight: string;
  cpu_weight: string;
}

export interface AtomicResponse<T> {
  success: boolean;
  query_time: number;
  data: T;
}

export interface AtomicTemplate {
  collection: any;
  contract: string;
  created_at_block: string;
  created_at_time: string;
  immutable_data: ImmutableData;
  name: string;
}
export interface ImmutableData {
  img: string;
  name: string;
  description: string;
}

@Injectable({
  providedIn: 'root'
})
export class WaxService {
  wax: waxjs.WaxJS;
  userAccount: string;
  userAccountDetails$: Observable<WaxAccount>;
  atomicCache: Record<string, ImmutableData> = {};
  constructor(public httpClient: HttpClient) {
    this.wax = new waxjs.WaxJS('https://wax.greymass.com', null, null, false);
    this.userAccountDetails$ = new Observable<WaxAccount>(obs => {
      const checkLoggedIn = () => {
        if (!this.userAccount) {
          setTimeout(checkLoggedIn, 100);
          return;
        }
        this.getAccount(this.userAccount).subscribe(accountInfo => {
          obs.next(accountInfo);
        }, err => obs.error(err));
      };
      checkLoggedIn();
    });
  }
  login(): Observable<string> {
    return new Observable(observer => {
      this.wax.isAutoLoginAvailable().then((isLoggedIn) => {
        if (!isLoggedIn) {
          this.wax.login().then((result: string) => {
            this.userAccount = result;

            observer.next(this.userAccount);
            observer.complete();
          }, (error) => {
            observer.error(error);
          });
        } else {
          // @ts-ignore
          this.userAccount = this.wax.userAccount;
          observer.next(this.userAccount);
        }
      }, (error) => {
        observer.error(error);
      });
    });
  }

  silentLogin(): Observable<string | boolean> {
    return new Observable(observer => {
      this.wax.isAutoLoginAvailable().then((isLoggedIn) => {
        if (!isLoggedIn) {
          observer.next(false);
        } else {
          // @ts-ignore
          this.userAccount = this.wax.userAccount;
          observer.next(this.userAccount);
        }
      }, (error) => {
        observer.error(error);
      });
    });
  }

  loadDrops(): Observable<TableRowDrop[]> {
    let nbResults = 0;
    const body = {
      json: true,
      code: 'atomicdropsx',
      scope: 'atomicdropsx',
      table: 'drops',
      table_key: '',
      lower_bound: '',
      upper_bound: '',
      index_position: 1,
      key_type: '',
      limit: 100,
      reverse: false,
      how_payer: false
    };
    return this.httpClient.post('https://chain.wax.io/v1/chain/get_table_rows', body).pipe(
      expand((result: TableRowsResult<TableRowDrop>) => {
        nbResults += result.rows.length;
        if (result.more && nbResults < Infinity) {
          body.lower_bound = result.rows[result.rows.length - 1].drop_id.toString();
          return this.httpClient.post('https://chain.wax.io/v1/chain/get_table_rows', body);
        } else {
          return EMPTY;
        }
      }),
      reduce((acc, value: TableRowsResult<TableRowDrop>) => {
        return acc.concat(value.rows.filter((row) => {
          const now = (new Date().getTime() / 1000);
          return (row.max_claimable === 0 || row.current_claimed < row.max_claimable) && (row.end_time === 0 || row.end_time > now);
        }));
      }, [])
    );
  }

  getWaxPrice(): Observable<number> {
    const body = {
      json: true,
      code: 'delphioracle',
      scope: 'waxpusd',
      table: 'datapoints',
      limit: 1,
    };
    return this.httpClient.post<TableRowsResult<TableRowPrice>>('https://chain.wax.io/v1/chain/get_table_rows', body).pipe(
      map((value: TableRowsResult<TableRowPrice>) => {
        return value.rows[0].median / 10000;
      })
    );
  }

  searchAsset(collectionName: string, template_id: number): Observable<ImmutableData> {
    if (`${collectionName}${template_id}` in this.atomicCache) {
      return of(this.atomicCache[`${collectionName}${template_id}`]);
    }
    const body = {
      json: true,
      code: 'atomicassets',
      scope: collectionName,
      table: 'templates',
      table_key: '',
      lower_bound: template_id,
      upper_bound: template_id,
      index_position: 1,
      key_type: '',
      limit: 100,
      reverse: false,
      how_payer: false
    };
    return this.httpClient.post(`https://wax2.api.atomicassets.io/atomicassets/v1/templates?collection_name=${collectionName}&ids=${template_id}`, body).pipe(
      map((result: AtomicResponse<AtomicTemplate[]>) => {
        this.atomicCache[`${collectionName}${template_id}`] = result.data[0].immutable_data;
        return result.data[0].immutable_data;
      })
    );
  }

  getAccount(accountName): Observable<WaxAccount> {
    const body = {
      account_name: accountName
    };
    return this.httpClient.post<WaxAccount>('https://chain.wax.io/v1/chain/get_account', body);
  }

  searchAccount(accountName: string): Observable<string[]> {
    const upperBound = accountName.padEnd(12, 'z');
    if (accountName.endsWith('.')) {
      accountName = accountName.slice(0, -1);
    }
    const body = {
      code: 'eosio',
      limit: 10,
      table: 'userres',
      lower_bound: accountName,
      upper_bound: upperBound
    };
    return this.httpClient.post<TableRowsResult<TableRowUser>>('https://chain.wax.io/v1/chain/get_table_by_scope', body).pipe(
      map(result => {
        return result.rows.map(user => user.scope);
      })
    );
  }

  getEosdtBalance(accountName: string): Observable<string> {
    // weosdttokens
    const body = {
      code: 'weosdttokens',
      account: accountName,
      symbol: 'WEOSDT'
    };
    return this.httpClient.post<string[]>('https://chain.wax.io/v1/chain/get_currency_balance', body).pipe(map(result => {
      if(result.length === 1) {
        return result[0];
      } else {
        return '0 WEOSDT';
      }
    }));
  }

}
